import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import debounce from 'lodash/debounce';
import { styled } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import EditorSearchIcon from '@atlaskit/icon/glyph/editor/search';
import TextField from '@atlaskit/textfield';
import { N500 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

import { PAGE_TITLE_DEFAULT_UNSPLASH_QUERY_WORD as CUSTOM_SITES_DEFAULT_UNSPLASH_QUERY_WORD } from '@confluence/custom-sites-extensions';

import type { UnsplashSearchBarComponentProps } from '../pageCoverPictureTypes';
import { DEFAULT_QUERY_WORD } from '../pageCoverPictureConstants';

import { getUnsplashPhotos } from './unsplashAPI';

const i18n = defineMessages({
	searchUnsplashPhotos: {
		id: 'page-cover-picture.search.unsplash.photos',
		description:
			'Placeholder text in search bar which advises user to search the Unsplash API for their photo.',
		defaultMessage: 'Search',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextFieldWrapper = styled.div({
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SearchIconWrapper = styled.div({
	marginLeft: token('space.050', '4px'),
	height: '24px',
	width: '27px',
});

export const UnsplashSearchBar = ({
	userOffline,
	setUnsplashInfoArray,
	pageNumber,
	queryWord,
	setShowPhotoSpinner,
	isCustomSitesPageTitleFFOn,
}: UnsplashSearchBarComponentProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const handleInput = () => {
		const searchBar = document.getElementById('UnsplashSearchBar') as HTMLInputElement;
		let queryKeyword = searchBar?.value;
		// if user deletes everything in search bar, we want to show the original image selection
		if (queryKeyword == '') {
			queryKeyword = isCustomSitesPageTitleFFOn
				? CUSTOM_SITES_DEFAULT_UNSPLASH_QUERY_WORD
				: DEFAULT_QUERY_WORD;
		}
		queryWord.current = queryKeyword;
		pageNumber.current = 1;
		void getUnsplashPhotos(queryKeyword, pageNumber.current).then((results) => {
			setUnsplashInfoArray(results);
			setShowPhotoSpinner(false);
		});
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'typed',
				actionSubject: 'headerImageSearch',
				actionSubjectId: 'headerImageSearchTyped',
				source: 'editorPageCoverPicture',
			},
		}).fire();
	};

	const debouncedHandleInput = debounce(handleInput, 350);

	const showSpinnerAndHandleInput = () => {
		setShowPhotoSpinner(true);
		debouncedHandleInput();
	};

	return (
		<TextFieldWrapper id="UnsplashSearchBarWrapper">
			<TextField
				testId="search-bar"
				id="UnsplashSearchBar"
				placeholder={formatMessage(i18n.searchUnsplashPhotos)}
				elemBeforeInput={
					<SearchIconWrapper>
						<EditorSearchIcon label="" primaryColor={token('color.icon', N500)} />
					</SearchIconWrapper>
				}
				onInput={showSpinnerAndHandleInput}
				isDisabled={userOffline}
				autoComplete="off"
			/>
		</TextFieldWrapper>
	);
};
